import React, { useEffect, useState } from 'react';
import { marketing } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Sparkles, Save, Eye, EyeOff, RefreshCw, Trash2, Plus, AlertTriangle, Clock, Users } from 'lucide-react';

/** Business dashboard tile — Tier-3 owner manages their slider "Send me deals" form. */
export default function OwnerSliderFormPanel() {
  const [state, setState] = useState({ loading: true, form: null, error: null });
  const [busy, setBusy] = useState(null);
  const [submissions, setSubmissions] = useState([]);

  const load = async () => {
    setState({ loading: true, form: null, error: null });
    try {
      const r = await marketing.sliderFormOwner();
      setState({ loading: false, form: r.form, error: null, businessName: r.businessName });
    } catch (err) {
      setState({ loading: false, form: null, error: err?.response?.data?.detail || 'Unable to load form.' });
    }
    try { setSubmissions(await marketing.sliderFormOwnerSubmissions()); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy('save');
    try {
      const r = await marketing.sliderFormOwnerUpdate({
        fields: state.form.fields,
        questions: state.form.questions,
      });
      toast({ title: r.flagged ? 'Edits under admin review' : 'Form updated', description: r.message });
      await load();
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.' }); }
    finally { setBusy(null); }
  };

  const toggleHidden = async () => {
    setBusy('toggle');
    try {
      await marketing.sliderFormOwnerUpdate({ hidden: state.form.status !== 'hidden' });
      await load();
    } catch (err) { toast({ title: 'Update failed', description: err?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const regenerate = async () => {
    if (!window.confirm('Replace your current questions with a fresh AI-generated set?')) return;
    setBusy('regen');
    try {
      const r = await marketing.sliderFormOwnerRegenerate();
      toast({ title: r.flagged ? 'Questions regenerated — under review' : 'Questions regenerated' });
      await load();
    } catch (err) { toast({ title: 'Regenerate failed', description: err?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const addQuestion = () => setState(s => ({ ...s, form: { ...s.form,
    questions: [...(s.form.questions || []), { id: `new-${Date.now()}`, type: 'text', prompt: '', options: null, required: false }].slice(0, 5)
  }}));

  const updateQ = (idx, patch) => setState(s => {
    const qs = [...(s.form.questions || [])];
    qs[idx] = { ...qs[idx], ...patch };
    return { ...s, form: { ...s.form, questions: qs } };
  });

  const removeQ = (idx) => setState(s => ({ ...s, form: { ...s.form,
    questions: (s.form.questions || []).filter((_, i) => i !== idx)
  }}));

  const updateField = (key, patch) => setState(s => ({ ...s, form: { ...s.form,
    fields: { ...(s.form.fields || {}), [key]: { ...(s.form.fields?.[key] || {}), ...patch } }
  }}));

  if (state.loading) {
    return <div className="p-8 text-center text-gray-500"><Loader2 className="animate-spin mx-auto" size={20}/></div>;
  }
  if (state.error) {
    return (
      <div className="p-6 bg-amber-500/10 border border-amber-500/30 rounded-xl text-sm text-amber-200" data-testid="slider-form-locked">
        <AlertTriangle size={16} className="inline mr-1"/> {state.error}
      </div>
    );
  }

  const f = state.form;
  const statusStyle = {
    live: 'bg-green-500/10 text-green-300 border-green-500/30',
    hidden: 'bg-gray-500/10 text-gray-300 border-gray-500/30',
    pending_review: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/30',
    draft: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  };

  return (
    <div className="space-y-5" data-testid="owner-slider-form-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-bold text-lg flex items-center gap-2"><Sparkles size={16}/> "Send me deals" Form</h2>
          <p className="text-xs text-gray-400 mt-1">
            Shown on the homepage slider when visitors check "Send me deals from <b>{state.businessName}</b>".
            Questions are AI-generated and editable. Edits are reviewed by admins before going live.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${statusStyle[f.status] || ''}`}>{f.status}</span>
          <button onClick={toggleHidden} disabled={busy === 'toggle'} data-testid="slider-form-toggle-hidden"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-neutral-700 hover:bg-neutral-800">
            {f.status === 'hidden' ? <><Eye size={12}/> Show</> : <><EyeOff size={12}/> Hide</>}
          </button>
        </div>
      </div>

      {f.pendingEdits && (
        <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-xs text-yellow-200 flex items-start gap-2">
          <Clock size={13} className="mt-0.5"/> Your recent edits are pending admin review. Your live form stays active until approved.
          {f.pendingEdits.flagReason && <span className="opacity-80">Reason: {f.pendingEdits.flagReason}</span>}
        </div>
      )}

      <section className="bg-[#0f0f0f] border border-[#1f1f1f] rounded-xl p-4 space-y-3">
        <div className="font-semibold text-sm">Contact fields</div>
        {['name', 'phone'].map(k => {
          const fc = f.fields?.[k] || { enabled: true, required: false, label: k };
          return (
            <div key={k} className="flex items-center gap-3 text-xs">
              <div className="w-16 capitalize">{k}</div>
              <input value={fc.label || ''} onChange={e => updateField(k, { label: e.target.value })}
                data-testid={`slider-field-label-${k}`}
                className="flex-1 bg-black border border-[#1f1f1f] rounded px-2 py-1 text-xs text-white"/>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!fc.enabled} onChange={e => updateField(k, { enabled: e.target.checked })} data-testid={`slider-field-enabled-${k}`}/> Show</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!fc.required} onChange={e => updateField(k, { required: e.target.checked })} data-testid={`slider-field-required-${k}`}/> Required</label>
            </div>
          );
        })}
        <div className="text-[10px] text-gray-500">Email is always required and cannot be disabled.</div>
      </section>

      <section className="bg-[#0f0f0f] border border-[#1f1f1f] rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Survey questions ({(f.questions || []).length} / 5)</div>
          <div className="flex items-center gap-2">
            <button onClick={regenerate} disabled={busy === 'regen'} data-testid="slider-form-regenerate"
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-neutral-700 hover:bg-neutral-800">
              {busy === 'regen' ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Regenerate with AI
            </button>
            {(f.questions || []).length < 5 && (
              <button onClick={addQuestion} data-testid="slider-form-add-question"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-600 hover:bg-green-500 text-black font-semibold">
                <Plus size={12}/> Add
              </button>
            )}
          </div>
        </div>

        {(f.questions || []).map((q, idx) => (
          <div key={q.id || idx} className="bg-black/40 border border-[#1a1a1a] rounded-lg p-3 space-y-2"
            data-testid={`slider-question-row-${idx}`}>
            <div className="flex items-center gap-2">
              <select value={q.type} onChange={e => updateQ(idx, { type: e.target.value, options: ['single_choice','multi_choice'].includes(e.target.value) ? (q.options || ['Option 1','Option 2']) : null })}
                className="bg-black border border-[#1f1f1f] rounded px-2 py-1 text-xs">
                <option value="single_choice">Single choice</option>
                <option value="multi_choice">Multi choice</option>
                <option value="rating">Rating (1-5)</option>
                <option value="text">Text</option>
              </select>
              <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!q.required} onChange={e => updateQ(idx, { required: e.target.checked })}/> Required</label>
              <button onClick={() => removeQ(idx)} className="ml-auto text-red-400 hover:text-red-300" title="Remove"
                data-testid={`slider-question-remove-${idx}`}>
                <Trash2 size={14}/>
              </button>
            </div>
            <input value={q.prompt} onChange={e => updateQ(idx, { prompt: e.target.value })}
              placeholder="Question prompt" data-testid={`slider-question-prompt-${idx}`}
              className="w-full bg-black border border-[#1f1f1f] rounded px-2 py-1.5 text-xs text-white"/>
            {['single_choice', 'multi_choice'].includes(q.type) && (
              <textarea value={(q.options || []).join('\n')} onChange={e => updateQ(idx, { options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                placeholder="One option per line" rows={3}
                data-testid={`slider-question-options-${idx}`}
                className="w-full bg-black border border-[#1f1f1f] rounded px-2 py-1.5 text-xs text-white"/>
            )}
          </div>
        ))}

        {(f.questions || []).length === 0 && (
          <div className="text-xs text-gray-500 py-3 text-center">No questions yet. Click "Regenerate with AI" or "Add".</div>
        )}
      </section>

      <div className="flex justify-end gap-2">
        <button onClick={save} disabled={busy === 'save'} data-testid="slider-form-save"
          className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-green-600 hover:bg-green-500 text-black font-bold text-sm disabled:opacity-60">
          {busy === 'save' ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Save changes
        </button>
      </div>

      <section className="bg-[#0f0f0f] border border-[#1f1f1f] rounded-xl p-4" data-testid="slider-form-submissions">
        <div className="font-semibold text-sm flex items-center gap-2"><Users size={14}/> Confirmed subscribers ({submissions.length})</div>
        {submissions.length === 0 ? (
          <div className="text-xs text-gray-500 py-3">No confirmed sign-ups yet. These will also appear in your main Contact List.</div>
        ) : (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs">
              <thead className="text-gray-500 text-left"><tr><th className="pb-1.5">Name</th><th className="pb-1.5">Email</th><th className="pb-1.5">Phone</th><th className="pb-1.5">Confirmed</th></tr></thead>
              <tbody>
                {submissions.slice(0, 50).map(s => (
                  <tr key={s.id} className="border-t border-[#1f1f1f]">
                    <td className="py-1.5">{s.contact?.name || '—'}</td>
                    <td className="py-1.5">{s.contact?.email}</td>
                    <td className="py-1.5">{s.contact?.phone || '—'}</td>
                    <td className="py-1.5 text-gray-500">{s.confirmedAt ? new Date(s.confirmedAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
